import React, { useEffect, useState, useMemo } from "react";
import {
    Loader2,
    RefreshCw,
    TrendingUp,
    TrendingDown,
    Euro,
    Package,
    Truck,
    Boxes,
    Users,
    Receipt,
    UserPlus,
    ArrowUpRight,
} from "lucide-react";
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
    BarChart,
    Bar,
    CartesianGrid,
    Cell,
} from "recharts";
import { toast } from "sonner";
import {
    ORDER_STATUS_LABELS,
    ORDER_STATUS_STYLES,
    formatDateTime,
    formatPrice,
} from "../lib/orderStatus";
import { LEAD_STATUS_LABELS, LEAD_STATUS_STYLES } from "../lib/leadStatus";

const BAR_COLORS = ["#7A8B76", "#B95C50", "#CFA059", "#8691A0", "#1C221F"];

export default function AdminDashboardHome({ apiAuth, onNavigate }) {
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(true);

    const load = async () => {
        setBusy(true);
        try {
            const { data } = await apiAuth("/admin/dashboard");
            setData(data);
        } catch {
            toast.error("Erro ao carregar dashboard");
        } finally {
            setBusy(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const chartData = useMemo(
        () =>
            (data?.sales_series || []).map((d) => ({
                ...d,
                label: new Date(d.date).toLocaleDateString("pt-PT", {
                    day: "2-digit",
                    month: "short",
                }),
            })),
        [data],
    );

    const topProductsData = useMemo(
        () => (data?.top_products || []).map((p) => ({ ...p, name: p.name })),
        [data],
    );

    if (busy && !data) {
        return (
            <div className="py-24 flex justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-brand-green" />
            </div>
        );
    }

    const kpis = data?.kpis || {};

    return (
        <div className="space-y-6" data-testid="admin-dashboard-home">
            {/* Header actions */}
            <div className="flex items-center justify-between gap-4 flex-wrap">
                <div className="text-xs uppercase tracking-widest font-bold text-brand-ink-soft">
                    Visão geral — últimos 30 dias
                </div>
                <button
                    type="button"
                    onClick={load}
                    className="inline-flex items-center gap-1.5 rounded-full bg-white hover:bg-brand-green-soft text-brand-ink px-3 py-2 text-xs font-semibold transition-colors"
                    data-testid="dashboard-refresh"
                >
                    <RefreshCw className={`h-3.5 w-3.5 ${busy ? "animate-spin" : ""}`} />
                    Atualizar
                </button>
            </div>

            {/* KPI cards */}
            <section className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <KpiCard
                    icon={Euro}
                    label="Receita 30 dias"
                    value={formatPrice(kpis.last30?.revenue || 0)}
                    delta={kpis.last30?.delta_pct}
                    bg="bg-brand-green"
                    white
                    testid="kpi-revenue-30d"
                />
                <KpiCard
                    icon={Receipt}
                    label="Encomendas 7d"
                    value={kpis.last7?.count ?? 0}
                    subtitle={formatPrice(kpis.last7?.revenue || 0)}
                    delta={kpis.last7?.delta_pct}
                    bg="bg-white"
                    accent="text-brand-green"
                    testid="kpi-orders-7d"
                />
                <KpiCard
                    icon={Truck}
                    label="A enviar"
                    value={kpis.orders_to_ship ?? 0}
                    subtitle={`${kpis.orders_in_transit ?? 0} em trânsito`}
                    bg="bg-brand-orange-soft"
                    accent="text-brand-orange"
                    clickable
                    onClick={() => onNavigate?.("orders")}
                    testid="kpi-to-ship"
                />
                <KpiCard
                    icon={Boxes}
                    label="Stock crítico"
                    value={kpis.low_stock ?? 0}
                    subtitle="itens abaixo do limiar"
                    bg={(kpis.low_stock ?? 0) > 0 ? "bg-red-50" : "bg-white"}
                    accent={(kpis.low_stock ?? 0) > 0 ? "text-red-600" : "text-brand-ink-soft"}
                    clickable
                    onClick={() => onNavigate?.("stock")}
                    testid="kpi-low-stock"
                />
            </section>

            <section className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <KpiCard
                    icon={UserPlus}
                    label="Novos leads 7d"
                    value={kpis.new_leads_7d ?? 0}
                    subtitle={`${kpis.total_leads ?? 0} no total`}
                    bg="bg-white"
                    accent="text-brand-orange"
                    clickable
                    onClick={() => onNavigate?.("leads")}
                    testid="kpi-new-leads"
                />
                <KpiCard
                    icon={Users}
                    label="Clientes"
                    value={kpis.customers_count ?? 0}
                    subtitle="registados na loja"
                    bg="bg-brand-ink"
                    white
                    clickable
                    onClick={() => onNavigate?.("customers")}
                    testid="kpi-customers"
                />
                <KpiCard
                    icon={Euro}
                    label="Hoje"
                    value={formatPrice(kpis.today?.revenue || 0)}
                    subtitle={`${kpis.today?.count ?? 0} encomendas`}
                    bg="bg-white"
                    accent="text-brand-green"
                    testid="kpi-today"
                />
                <KpiCard
                    icon={Package}
                    label="Encomendas totais"
                    value={Object.values(data?.by_status || {}).reduce((a, b) => a + b, 0)}
                    subtitle="desde sempre"
                    bg="bg-brand-cream"
                    accent="text-brand-ink"
                    testid="kpi-orders-total"
                />
            </section>

            {/* Sales chart */}
            <section className="rounded-2xl bg-white p-6 shadow-soft ring-1 ring-brand-green/10">
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <div className="text-xs uppercase tracking-widest font-bold text-brand-green">
                            Vendas diárias
                        </div>
                        <div className="text-sm text-brand-ink-soft">
                            Últimos 30 dias
                        </div>
                    </div>
                </div>
                <div className="h-64" data-testid="dashboard-sales-chart">
                    <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e8e2d6" />
                            <XAxis
                                dataKey="label"
                                interval={"preserveStartEnd"}
                                tick={{ fontSize: 11, fill: "#5a5a55" }}
                            />
                            <YAxis tick={{ fontSize: 11, fill: "#5a5a55" }} />
                            <Tooltip
                                contentStyle={{
                                    borderRadius: 12,
                                    border: "1px solid #d5d1c5",
                                    fontSize: 12,
                                }}
                                formatter={(value, key) =>
                                    key === "revenue"
                                        ? [formatPrice(value), "Receita"]
                                        : [value, "Encomendas"]
                                }
                            />
                            <Line
                                type="monotone"
                                dataKey="revenue"
                                stroke="#7A8B76"
                                strokeWidth={2.5}
                                dot={false}
                                activeDot={{ r: 5 }}
                            />
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            </section>

            {/* Top products & Latest lists */}
            <section className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="rounded-2xl bg-white p-6 shadow-soft ring-1 ring-brand-green/10">
                    <div className="flex items-center gap-2 text-xs uppercase tracking-widest font-bold text-brand-green">
                        <TrendingUp className="h-3.5 w-3.5" /> Top 5 sabores (30d)
                    </div>
                    {topProductsData.length === 0 ? (
                        <div className="mt-8 text-center text-sm text-brand-ink-soft">
                            Ainda sem vendas nos últimos 30 dias.
                        </div>
                    ) : (
                        <div className="h-56 mt-4" data-testid="dashboard-top-products">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart
                                    data={topProductsData}
                                    layout="vertical"
                                    margin={{ top: 5, right: 20, left: 10, bottom: 5 }}
                                >
                                    <XAxis type="number" tick={{ fontSize: 11, fill: "#5a5a55" }} />
                                    <YAxis
                                        type="category"
                                        dataKey="name"
                                        tick={{ fontSize: 11, fill: "#1C221F" }}
                                        width={100}
                                    />
                                    <Tooltip
                                        contentStyle={{
                                            borderRadius: 12,
                                            border: "1px solid #d5d1c5",
                                            fontSize: 12,
                                        }}
                                        formatter={(v, k) =>
                                            k === "units" ? [v, "unidades"] : [formatPrice(v), "receita"]
                                        }
                                    />
                                    <Bar dataKey="units" radius={[0, 8, 8, 0]}>
                                        {topProductsData.map((_, i) => (
                                            <Cell key={i} fill={BAR_COLORS[i % BAR_COLORS.length]} />
                                        ))}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    )}
                </div>

                <div className="rounded-2xl bg-white p-6 shadow-soft ring-1 ring-brand-green/10">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-xs uppercase tracking-widest font-bold text-brand-orange">
                            <Receipt className="h-3.5 w-3.5" /> Últimas encomendas
                        </div>
                        <button
                            type="button"
                            onClick={() => onNavigate?.("orders")}
                            className="inline-flex items-center gap-1 text-xs font-semibold text-brand-green hover:underline"
                            data-testid="dashboard-see-orders"
                        >
                            Ver todas <ArrowUpRight className="h-3 w-3" />
                        </button>
                    </div>
                    <ul className="mt-3 divide-y divide-brand-green/10">
                        {(data?.latest_orders || []).length === 0 ? (
                            <li className="py-6 text-center text-sm text-brand-ink-soft">
                                Ainda sem encomendas.
                            </li>
                        ) : (
                            data.latest_orders.map((o) => (
                                <li key={o.id} className="py-2.5 flex items-center gap-3">
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm font-semibold text-brand-ink truncate">
                                            #{o.order_number || o.id?.slice(0, 8)} —{" "}
                                            {o.customer_name || o.customer_email || "Sem cliente"}
                                        </div>
                                        <div className="text-[11px] text-brand-ink-soft">
                                            {formatDateTime(o.created_at)}
                                        </div>
                                    </div>
                                    <span
                                        className={`text-[10px] uppercase font-bold rounded-full px-2 py-0.5 ${
                                            ORDER_STATUS_STYLES[o.order_status] ||
                                            "bg-brand-cream text-brand-ink"
                                        }`}
                                    >
                                        {ORDER_STATUS_LABELS[o.order_status] || o.order_status}
                                    </span>
                                    <span className="text-sm font-bold text-brand-ink w-20 text-right">
                                        {formatPrice(o.amount || 0)}
                                    </span>
                                </li>
                            ))
                        )}
                    </ul>
                </div>
            </section>

            {/* Latest leads */}
            <section className="rounded-2xl bg-white p-6 shadow-soft ring-1 ring-brand-green/10">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-xs uppercase tracking-widest font-bold text-brand-green">
                        <UserPlus className="h-3.5 w-3.5" /> Últimos leads
                    </div>
                    <button
                        type="button"
                        onClick={() => onNavigate?.("leads")}
                        className="inline-flex items-center gap-1 text-xs font-semibold text-brand-green hover:underline"
                        data-testid="dashboard-see-leads"
                    >
                        Ver todos <ArrowUpRight className="h-3 w-3" />
                    </button>
                </div>
                <ul className="mt-3 divide-y divide-brand-green/10">
                    {(data?.latest_leads || []).length === 0 ? (
                        <li className="py-6 text-center text-sm text-brand-ink-soft">
                            Ainda sem leads.
                        </li>
                    ) : (
                        data.latest_leads.map((l) => (
                            <li key={l.id} className="py-2.5 flex items-center gap-3">
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm font-semibold text-brand-ink truncate">
                                        {l.name}
                                    </div>
                                    <div className="text-[11px] text-brand-ink-soft truncate">
                                        {l.email} · {formatDateTime(l.created_at)}
                                    </div>
                                </div>
                                <span className="text-[10px] uppercase font-bold rounded-full px-2 py-0.5 bg-brand-cream text-brand-ink">
                                    {l.interest}
                                </span>
                                <span
                                    className={`text-[10px] uppercase font-bold rounded-full px-2 py-0.5 ${
                                        LEAD_STATUS_STYLES[l.status] || "bg-brand-cream text-brand-ink"
                                    }`}
                                >
                                    {LEAD_STATUS_LABELS[l.status] || l.status}
                                </span>
                            </li>
                        ))
                    )}
                </ul>
            </section>
        </div>
    );
}

const KpiCard = ({
    icon: Icon,
    label,
    value,
    subtitle,
    delta,
    bg,
    accent,
    white,
    clickable,
    onClick,
    testid,
}) => {
    const Cmp = clickable ? "button" : "div";
    return (
        <Cmp
            type={clickable ? "button" : undefined}
            onClick={clickable ? onClick : undefined}
            data-testid={testid}
            className={`rounded-2xl ${bg} p-5 shadow-soft ring-1 ring-brand-green/5 text-left w-full transition-all ${
                clickable ? "hover:shadow-pop hover:-translate-y-0.5 cursor-pointer" : ""
            }`}
        >
            <div className="flex items-start justify-between">
                <Icon className={`h-5 w-5 ${accent || (white ? "text-white/90" : "text-brand-ink-soft")}`} />
                {typeof delta === "number" && (
                    <span
                        className={`inline-flex items-center gap-0.5 text-[10px] font-bold rounded-full px-1.5 py-0.5 ${
                            delta >= 0
                                ? "bg-brand-green-soft text-brand-green"
                                : "bg-red-100 text-red-600"
                        }`}
                    >
                        {delta >= 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                        {Math.abs(delta)}%
                    </span>
                )}
            </div>
            <div
                className={`mt-3 text-[11px] uppercase tracking-widest font-bold ${
                    white ? "text-white/70" : "text-brand-ink-soft"
                }`}
            >
                {label}
            </div>
            <div
                className={`mt-1 font-display font-medium text-2xl md:text-3xl ${
                    white ? "text-white" : "text-brand-ink"
                }`}
            >
                {value}
            </div>
            {subtitle && (
                <div
                    className={`mt-1 text-xs ${white ? "text-white/70" : "text-brand-ink-soft"}`}
                >
                    {subtitle}
                </div>
            )}
        </Cmp>
    );
};
